/**
 * One grid template, shared by the column header, every task row and the inline
 * add row.
 *
 * It has to be shared rather than repeated because each status group is its own
 * `<ul>`: separate grids size their columns independently, so two groups would
 * silently fall out of alignment the moment one had a longer title than the
 * other. Every row on the page sizing against the same fixed tracks is what
 * makes this read as a table.
 *
 * Written as one literal string, not composed from parts at runtime: Tailwind
 * scans source text for whole class names, so a template built from variables
 * would never be generated.
 *
 * Columns: select toggle | title and counts | assignees | status | due |
 * priority | row actions.
 *
 * The assignee track is 4rem rather than one avatar's 1.25rem. Three 1.25rem
 * avatars plus a `+N` chip, overlapping by 0.375rem each, measure 3.875rem --
 * so anything narrower spills into the due date. Both the mobile and `sm:`
 * templates carry it, and they have to change together or the header and rows
 * disagree below `sm:`.
 *
 * The trailing action track is 2.75rem: two 1.25rem buttons with a 0.125rem gap.
 * It is on both templates on purpose. Below `sm:` the due and priority tracks
 * drop off -- and those buttons become the only inline edit a phone has, so
 * that is exactly where they are least droppable. Trailing rather than leading
 * because the fixed track never moves as the columns before it change, and
 * because it is where TaskComposer already puts its own row action.
 *
 * Due and priority stay at their old widths even though they are now buttons.
 * The hit areas grow into the gutter instead (`-mx-1 px-1` eats half of the
 * `gap-x-2` on each side), which widens the target without moving the text.
 *
 * The status track is the one column that changes width rather than dropping
 * out below `sm:`: 5.25rem fits the dot plus "In progress", the longest label,
 * and 1rem fits the dot alone. It survives the narrow template because the
 * status heading it would otherwise be redundant with is not on screen at all
 * when grouping is off -- where due and priority are merely useful, this can be
 * the only statement of what state a task is in.
 *
 * It leads the two date-ish columns rather than trailing them. Status and the
 * title are the pair a scan reads together -- what the task is and what state
 * it is in -- and with grouping turned off this cell is the only thing on the
 * row that answers the second half, so it belongs next to the first rather than
 * five tracks away at the edge. Due and priority qualify a task; they do not
 * identify it.
 *
 * It used to sit after priority, so that the two hover buttons that also move a
 * task's status were next to the cell that reads it. That adjacency is worth
 * less than the reading order: the buttons name their destination in a tooltip
 * and are found by hovering the row, not by being beside the status.
 *
 * TASK_GRID_WITH_PROJECT below puts status one track earlier still, ahead of
 * the project name, for a reason that only applies to that page; see its
 * comment.
 */
export const TASK_GRID =
  'grid items-center gap-x-2 grid-cols-[1rem_minmax(0,1fr)_4rem_1rem_2.75rem] sm:grid-cols-[1rem_minmax(0,1fr)_4rem_5.25rem_3.5rem_4.5rem_2.75rem]';

/**
 * The same table with a project column, for the cross-project Tasks page.
 *
 * A second literal rather than a template built from TASK_GRID: Tailwind scans
 * source text for whole class names, so anything composed at runtime is never
 * generated. The two have to be edited together.
 *
 * The project track is 7rem and `sm:` only. Below that breakpoint the due and
 * priority tracks are already dropped, and a project name is the next least
 * useful thing on a phone -- every row on the Tasks page belongs to some
 * project, so the column carries no information the title does not, where a due
 * date does.
 *
 * Status comes straight after the title here, one track earlier than on the
 * project list, which is the one place the two templates disagree about order.
 * Both put status ahead of the date columns for the same reason -- what a task
 * is and what state it is in are the pair a scan reads together -- but here the
 * project name is the grouping around that pair, so it reads after them rather
 * than between them.
 *
 * That difference carries down to the narrow template too -- with the project
 * hidden below `sm:` the status dot follows the title directly instead of
 * trailing the avatars -- so this one is not TASK_GRID's mobile template with a
 * column added. TaskRow and TaskList's header both branch on `showProject` to
 * match; all three have to move together.
 *
 * Columns: select toggle | title and counts | status | project | assignees |
 * due | priority | row actions.
 */
export const TASK_GRID_WITH_PROJECT =
  'grid items-center gap-x-2 grid-cols-[1rem_minmax(0,1fr)_1rem_4rem_2.75rem] sm:grid-cols-[1rem_minmax(0,1fr)_5.25rem_7rem_4rem_3.5rem_4.5rem_2.75rem]';
